import { AxiosPromise, AxiosResponse } from 'axios';
import { apiService, manageFilters } from '@app/services';
import { defaultOrgId } from '@app/helpers';
import { IActiveFilter, IgetCertificateRespose } from '@app/models';

class ApplicationsService {

    readonly appBaseUrl: string = '/api/directory/Applications';
    readonly appsOrganizationsUrl: string = '/api/directory/Organizations';

    uploadImage(data: any, handleError: Function, handleSuccess: Function): AxiosPromise {
        const apiUrl: string = `/api/directory/Applications(${defaultOrgId})/UploadImage`;

        return apiService.post(apiUrl, data,
            { onError: handleError, onSuccess: handleSuccess, headers: { 'Content-Type': 'multipart/form-data' } })
            .catch();
    }

    getCertificate(appId: string, handleError: Function, handleSuccess: Function): AxiosPromise<AxiosResponse<IgetCertificateRespose>> {
        const apiUrl = `${this.appBaseUrl}(${appId})/Keys`;
        return apiService.get(apiUrl,
            { onError: handleError, onSuccess: handleSuccess, headers: { 'Content-Type': 'application/json' } })
            .catch();
    }

    getOrganizations(organizationId: string): AxiosPromise {
        const apiUrl: string = `${this.appsOrganizationsUrl}(${organizationId})?$expand=PasswordPolicy`;

        return apiService.get(apiUrl)
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getAdminSuppliedCredentials(appId: string, handleSuccess: Function): AxiosPromise {
        const apiUrl: string = `${this.appBaseUrl}(${appId})/AdministratorSuppliedCredentials`;

        return apiService.get(apiUrl,
            {
                onSuccess: handleSuccess
            })
            .catch();
    }

    updateSuppliedCredential(appId: string, data: any): AxiosPromise {
        const apiUrl: string = `${this.appBaseUrl}(${appId})/UpdateSuppliedCredential`;

        return apiService.post(apiUrl, data)
            .catch();
    }

    // PERMISSIONS

    getGroupsNotInAppUrl = (
        appId: string,
        orderBy: string,
        asc: string,
        top: number,
        skip: number,
        isReset: boolean,
        filters?: IActiveFilter[],
        organizationId?: string
    ): AxiosPromise => {

        return apiService
            .get(`${this.appBaseUrl}(${appId})/NotInGroup?`, {
                params: {
                    $orderBy: orderBy + ' ' + asc,
                    $top: top,
                    $skip: skip,
                    $count: isReset,
                    ...(filters ? { $filter: manageFilters(filters, organizationId) } : { $filter: manageFilters([], organizationId) })
                }
            })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    addGroupUrl(appId: string, handleError: Function, handleSuccess: Function, groupIds: string[], orgFilter: string) {
        const apiUrl: string = `${this.appBaseUrl}(${appId})/AddGroup?organizationId=${orgFilter}`;

        return apiService.post(apiUrl,
            JSON.stringify({ Group: groupIds }),
            { onError: handleError, onSuccess: handleSuccess, headers: { 'Content-Type': 'application/json' } })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    removeGroupUrl(appId: string, handleError: Function, handleSuccess: Function, groupIds: string[], orgFilter: string) {
        const apiUrl: string = `${this.appBaseUrl}(${appId})/RemoveGroups?organizationId=${orgFilter}`;

        return apiService.post(apiUrl,
            JSON.stringify({ Groups: groupIds }),
            { onError: handleError, onSuccess: handleSuccess, headers: { 'Content-Type': 'application/json' } })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    // CERTIFICATES
    uploadSigningCertificate(appId: string, data: FormData | null, password: string, onError: Function, onSuccess: Function) {
        const headers = { 'X-Pfx-Password': password, 'Content-type': 'multipart/form-data' };

        return apiService.post(
            `${this.appBaseUrl}(${appId})/UpdateSigningKey`,
            data,
            { onError, onSuccess, headers }
        );
    }

    generateSigningCertificate(appId: string, signSubject: string, onError: Function, onSuccess: Function) {
        const headers = { 'X-Signing-Subject': signSubject };

        return apiService.post(
            `${this.appBaseUrl}(${appId})/UpdateSigningKey`,
            null,
            { onError, onSuccess, headers }
        );
    }

    uploadEncryptionCertificate(appId: string, data: FormData, onError: Function, onSuccess: Function) {
        return apiService.post(
            `${this.appBaseUrl}(${appId})/UpdateEncryptionKey`,
            data,
            { onError, onSuccess }
        );
    }

    removeEncryptionCertificate(appId: string, onError: Function, onSuccess: Function) {
        return apiService.post(
            `${this.appBaseUrl}(${appId})/UpdateEncryptionKey`,
            '',
            { onError, onSuccess }
        );
    }

    // Microsoft Office app
    customActionUrl(appId: string) {
        return `/api/directory/applications(${appId})/CustomAction`;
    }

    postVerifyOffice365Credentials(appId: string, data: any, onError: Function, onSuccess: Function) {
        return apiService.post(
            this.customActionUrl(appId),
            data,
            { onError, onSuccess }
        );
    }

    postUpdateOffice365ToPassly(appId: string, data: any, onSuccess: Function) {
        return apiService.post(
            this.customActionUrl(appId),
            data,
            { onSuccess }
        );
    }

    postGetOffice365ConfigOptions(appId: string, data: any, onSuccess: Function) {
        return apiService.post(
            this.customActionUrl(appId),
            data,
            { onSuccess }
        );
    }

    postUpdateOffice365Config(appId: string, data: any, onError: Function, onSuccess: Function) {
        return apiService.post(
            this.customActionUrl(appId),
            data,
            { onError, onSuccess }
        );
    }

    postDisconnectOffice365(appId: string, data: any, onSuccess: Function) {
        return apiService.post(
            this.customActionUrl(appId),
            data,
            { onSuccess }
        );
    }


}

export const applicationsService = new ApplicationsService();
